import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { REPORT } from "@/constants/testIds";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from "recharts";
import { Timer, TrendingDown, ClipboardCheck, Search } from "lucide-react";

const CHART_COLORS = ["#B84221", "#D97736", "#78866B", "#1E293B", "#EAB308", "#94A3B8"];

function Big({ label, value, sub, icon: Icon, tid, tint = "primary" }) {
  return (
    <div
      data-testid={tid}
      className="paper-card p-5 hover:-translate-y-0.5 transition-transform"
    >
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <div
          className={`w-8 h-8 rounded-lg grid place-items-center ${
            tint === "primary"
              ? "bg-terracotta/10 text-terracotta"
              : tint === "amber"
                ? "bg-amber-50 text-amber-600"
                : tint === "sage"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-stone-100 text-stone-600"
          }`}
        >
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-serif text-4xl leading-none text-stone-900 mt-3">
        {value}
      </div>
      {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Reports() {
  const [rep, setRep] = useState(null);
  useEffect(() => {
    api.get("/reports/summary").then(({ data }) => setRep(data));
  }, []);
  if (!rep)
    return <div className="text-stone-500 text-sm">Loading reports…</div>;
  return (
    <div data-testid={REPORT.page} className="space-y-6 animate-fade-up">
      <div>
        <div className="overline">Reporting</div>
        <h1 className="font-serif text-4xl text-stone-900 mt-1">
          The month, in cloth
        </h1>
        <p className="text-stone-500 text-sm mt-2">
          What's converting, what's lagging, and where Saheli is helping most.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <Big
          tid={REPORT.avgResponse}
          label="Avg response time"
          value={rep.avg_response_minutes ? `${rep.avg_response_minutes}m` : "—"}
          sub="Across all replied inquiries"
          icon={Timer}
          tint="sage"
        />
        <Big
          tid={REPORT.followupRate}
          label="Follow-up completion"
          value={`${rep.followup_completion_rate}%`}
          sub="Replied within 24 hours"
          icon={ClipboardCheck}
          tint="primary"
        />
        <Big
          tid={REPORT.matchRate}
          label="Match tool usage"
          value={`${rep.match_tool_usage_rate}%`}
          sub={`${rep.match_tool_searches} searches · ${rep.match_tool_linked_to_inquiry} from inquiries`}
          icon={Search}
          tint="amber"
        />
        <Big
          tid={REPORT.lostSlow}
          label="Lost to slow reply"
          value={rep.lost_to_slow_reply}
          sub="Never replied within 24h"
          icon={TrendingDown}
          tint="amber"
        />
      </div>

      <div data-testid={REPORT.conversionByType} className="paper-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Conversion by inquiry type</div>
            <h3 className="font-serif text-xl text-stone-800 mt-1">
              Which questions become orders
            </h3>
          </div>
        </div>
        {rep.conversion_by_type?.length ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={rep.conversion_by_type}
                margin={{ top: 10, right: 10, bottom: 0, left: -10 }}
              >
                <XAxis
                  dataKey="type"
                  tick={{ fontSize: 11, fill: "#78716c" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#78716c" }}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 100]}
                  unit="%"
                />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #e7e5e4",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(v, n, p) => [`${v}%`, "Conversion"]}
                />
                <Bar dataKey="rate" radius={[8, 8, 0, 0]}>
                  {rep.conversion_by_type.map((row) => (
                    <Cell
                      key={row.type}
                      fill={
                        CHART_COLORS[
                          rep.conversion_by_type.indexOf(row) % CHART_COLORS.length
                        ]
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-sm text-stone-400 py-8 text-center">
            No inquiries yet.
          </div>
        )}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
          {rep.conversion_by_type?.map((row) => (
            <div
              key={row.type}
              className="border border-stone-200 rounded-lg p-2.5"
            >
              <div className="text-stone-500">{row.type}</div>
              <div className="text-stone-900 mt-0.5">
                {row.converted}/{row.total} ·{" "}
                <span className="text-terracotta font-medium">{row.rate}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
